import React, { useContext, useState, useMemo, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Autocomplete,
    Chip,
    Alert,
    Slider,
    Tooltip,
    Divider,
    TableRow,
    TableCell,
    IconButton,
} from '@mui/material';
import { Info } from '@phosphor-icons/react';
import PercentIcon from '@mui/icons-material/Percent';
import CloseIcon from '@mui/icons-material/Close';
import { ThemeContext } from 'styled-components';
import { CsrfContext } from '../../../providers/CsrfProvider';
import { updateBillRule, deleteBillRule } from '../../../services/bill/BillRules';
import { useQuery, useQueryClient } from 'react-query';
import { listTemplateParticipants } from '../../../services/template/Participants';
import { getTracks } from '../../../services/bill/BillingTracks';
import ParticipantSlider from './ParticipantSlider';
import WithActions from '../../common/components/Wrappers/WithActions';
import FormatNumber from '../../../utils/formatters/FormatNumber';

const numberToWords = (num) => {
    const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
                  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

    if (num === 0) return 'zero';
    if (num < 20) return ones[num];
    if (num < 100) {
        const ten = Math.floor(num / 10);
        const one = num % 10;
        return tens[ten] + (one > 0 ? '-' + ones[one] : '');
    }
    return num.toString(); // For 100+, just return the number
};

const EditRuleForm = ({ templateId, rule, open, onClose, onSuccess, onDelete, isEditing, onCancel }) => {
    const theme = useContext(ThemeContext);
    const { csrfToken } = useContext(CsrfContext);
    const queryClient = useQueryClient();

    // Determine if we're in inline mode or dialog mode
    const inlineMode = isEditing === true;
    const isOpen = inlineMode || open;

    const [ruleType, setRuleType] = useState(rule?.rule_type || 'minus');
    const [label, setLabel] = useState(rule?.label ?? '');
    const [amount, setAmount] = useState('');
    const [rulePercentage, setRulePercentage] = useState('');
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const [participantPercentages, setParticipantPercentages] = useState({});
    const [participantInputValue, setParticipantInputValue] = useState('');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const previousRulePercentageRef = useRef(null);
    const isInitializingRef = useRef(false);

    // Initialize form with rule data when rule changes or dialog opens
    useEffect(() => {
        if (rule && isOpen) {
            isInitializingRef.current = true; // Mark as initializing to prevent scaling
            setRuleType(rule.rule_type || 'minus');
            setLabel(rule.label ?? '');
            setAmount(rule.amount ? rule.amount.toString() : '');

            // Handle rule_percentage - check for null/undefined, but allow 0 as a valid value
            // The backend uses omitempty, so rule_percentage might not be in the JSON if it's nil
            if (rule.rule_type === 'percent') {
                // Get rule_percentage - it might be undefined if omitted from JSON
                let rulePct = rule.rule_percentage;

                // If rule_percentage is missing, try to calculate it from participant percentages
                if (rulePct == null && rule.participants && Array.isArray(rule.participants) && rule.participants.length > 0) {
                    // Sum up all participant percentages
                    const totalPct = rule.participants.reduce((sum, p) => {
                        return sum + (p.percentage || 0);
                    }, 0);
                    // If the sum is reasonable (between 0 and 100), use it
                    if (totalPct > 0 && totalPct <= 100) {
                        rulePct = totalPct;
                    }
                }

                // Convert to number and check if it's valid (including 0)
                const numPct = rulePct != null ? Number(rulePct) : null;
                if (numPct !== null && !isNaN(numPct) && numPct >= 0 && numPct <= 100) {
                    setRulePercentage(numPct.toString());
                    previousRulePercentageRef.current = numPct; // Store initial value
                } else {
                    setRulePercentage('');
                    previousRulePercentageRef.current = null;
                }
            } else {
                // For non-percent rules (minus rules), clear the rule percentage
                setRulePercentage('');
                previousRulePercentageRef.current = null;
                // Also clear participant percentages
                setParticipantPercentages({});
            }

            // Set selected participants from rule (including vacancies)
            if (rule.participants && Array.isArray(rule.participants)) {
                const participantsList = [];
                const percentages = {};
                
                rule.participants.forEach(p => {
                    if (p.user && p.user.id) {
                        // Regular participant with user
                        participantsList.push({
                            id: p.user.id,
                            email: p.user.email || '',
                            isVacancy: false,
                        });
                        if (p.percentage != null) {
                            percentages[p.user.id] = p.percentage;
                        }
                    } else if (p.track && (!p.track.user_id || p.track.user_id === '')) {
                        // Vacancy (has track but no user)
                        const trackId = p.track.id || p.track_id;
                        const trackIndex = p.track.track_index;
                        participantsList.push({
                            id: `vacancy-${trackId}`,
                            email: `Vacancy - Track ${trackIndex || '?'}`,
                            isVacancy: true,
                            trackId: trackId,
                            trackIndex: trackIndex,
                        });
                        if (p.percentage != null) {
                            percentages[`vacancy-${trackId}`] = p.percentage;
                        }
                    }
                });
                
                setSelectedParticipants(participantsList);

                // If no percentages set, default to even split of the rule percentage
                if (participantsList.length > 0 && Object.keys(percentages).length === 0 && rule.rule_type === 'percent') {
                    // Use the rule percentage if available, otherwise default to 100
                    const rulePct = rule.rule_percentage || 100;
                    const equalPercent = rulePct / participantsList.length;
                    participantsList.forEach(p => {
                        if (p && p.id) {
                            percentages[p.id] = equalPercent;
                        }
                    });
                }

                setParticipantPercentages(percentages);
            } else {
                setSelectedParticipants([]);
                setParticipantPercentages({});
            }
            setError(null);
            // Reset initialization flag after a short delay to allow state to settle
            setTimeout(() => {
                isInitializingRef.current = false;
            }, 100);
        }
    }, [rule, isOpen]);

    // Scale participant percentages proportionally when rule percentage changes
    useEffect(() => {
        // Only scale if:
        // 1. Not initializing
        // 2. Rule type is percent
        // 3. We have participants
        // 4. We have a previous rule percentage to scale from
        // 5. The new rule percentage is valid
        if (isInitializingRef.current || ruleType !== 'percent' || selectedParticipants.length === 0) {
            return;
        }

        const newRulePct = rulePercentage ? parseFloat(rulePercentage) : null;
        const prevRulePct = previousRulePercentageRef.current;

        if (newRulePct != null && !isNaN(newRulePct) && prevRulePct != null && prevRulePct > 0 && newRulePct > 0) {
            // Calculate scale factor
            const scaleFactor = newRulePct / prevRulePct;

            // Scale all participant percentages proportionally
            setParticipantPercentages(prev => {
                const scaled = {};
                let hasChanges = false;

                Object.keys(prev).forEach(key => {
                    const currentPct = prev[key];
                    if (currentPct != null && !isNaN(currentPct)) {
                        const scaledPct = currentPct * scaleFactor;
                        scaled[key] = scaledPct;
                        if (Math.abs(scaledPct - currentPct) > 0.01) { // Only mark as changed if difference is significant
                            hasChanges = true;
                        }
                    } else {
                        scaled[key] = currentPct;
                    }
                });

                // Only update if there are actual changes
                return hasChanges ? scaled : prev;
            });

            // Update the previous value
            previousRulePercentageRef.current = newRulePct;
        } else if (newRulePct != null && !isNaN(newRulePct) && newRulePct > 0) {
            // If we don't have a previous value but have a new valid value,
            // initialize all participant percentages evenly
            if (prevRulePct == null && selectedParticipants.length > 0) {
                const equalPercent = newRulePct / selectedParticipants.length;
                const newPercentages = {};
                selectedParticipants.forEach(p => {
                    const key = p.id || p.email;
                    if (key) {
                        newPercentages[key] = equalPercent;
                    }
                });
                setParticipantPercentages(newPercentages);
            }
            previousRulePercentageRef.current = newRulePct;
        }
    }, [rulePercentage, ruleType, selectedParticipants.length]);

    // Fetch participants for this bill
    const queryKey = useMemo(() => {
        if (!templateId) return ['participants', 'none'];
        const id = String(templateId);
        if (id === 'undefined' || id === 'null' || id === 'none') return ['participants', 'none'];
        return ['participants', id];
    }, [templateId]);

    const { data: participantsData, isLoading: isLoadingParticipants } = useQuery(
        queryKey,
        listTemplateParticipants(csrfToken),
        {
            enabled: !!templateId && !!csrfToken && templateId !== 'none' && isOpen,
            refetchOnMount: true,
            refetchOnWindowFocus: false,
        }
    );

    // Ensure participants is always an array
    const participants = Array.isArray(participantsData) ? participantsData : [];

    // Fetch tracks to identify vacancies
    const { data: tracks } = useQuery(
        ['billingTracks', String(templateId)],
        () => getTracks(String(templateId), csrfToken),
        {
            enabled: !!templateId && !!csrfToken && templateId !== 'none' && isOpen,
            refetchOnWindowFocus: false,
        }
    );

    const handleClose = () => {
        setRuleType('minus');
        setLabel('');
        setAmount('');
        setRulePercentage('');
        setSelectedParticipants([]);
        setParticipantPercentages({});
        setError(null);
        setShowAdvanced(false);
        if (inlineMode && onCancel) {
            onCancel();
        } else if (onClose) {
            onClose();
        }
    };

    const handleAmountChange = (e) => {
        const value = e.target.value;
        if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
            setAmount(value);
        }
    };

    const handleAmountKeyDown = (e) => {
        // Handle arrow key presses to increment/decrement by 1 while preserving decimals
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const currentValue = amount === '' ? 0 : parseFloat(amount);
            const newValue = e.key === 'ArrowUp' ? currentValue + 1 : Math.max(0, currentValue - 1);
            setAmount(newValue.toString());
        }
    };

    const handlePercentageChange = (userId, value) => {
        const numValue = value === '' ? null : parseFloat(value);
        if (numValue === null || (!isNaN(numValue) && numValue >= 0 && numValue <= 100)) {
            setParticipantPercentages(prev => ({
                ...prev,
                [userId]: numValue === null ? null : numValue
            }));
        }
    };

    const calculateTotalPercentage = () => {
        return Object.values(participantPercentages).reduce((sum, pct) => {
            return sum + (pct || 0);
        }, 0);
    };

    const handleSubmit = async () => {
        setError(null);

        // Validation
        if (ruleType === 'minus' && (!amount || parseFloat(amount) <= 0)) {
            setError('Minus rule requires a positive amount');
            return;
        }

        if (ruleType === 'percent' && (!rulePercentage || parseFloat(rulePercentage) <= 0 || parseFloat(rulePercentage) > 100)) {
            setError('Percent rule requires a rule percentage between 0 and 100');
            return;
        }

        if (selectedParticipants.length === 0) {
            setError('Rule must have at least one participant');
            return;
        }

        if (ruleType === 'percent') {
            const totalPct = calculateTotalPercentage();
            if (totalPct > 100.01) { // Allow small floating point errors
                setError(`Participant percentages cannot exceed 100% (currently ${FormatNumber(totalPct)}%)`);
                return;
            }
            if (totalPct === 0) {
                setError('Percent rule requires at least one participant with a percentage');
                return;
            }
        }

        setSubmitting(true);

        try {
            // Prepare participant data - can be user IDs, emails, or track IDs (for vacancies)
            const participantUserIDs = [];
            const participantEmails = [];
            const participantTrackIDs = [];

            selectedParticipants.forEach(p => {
                if (p.isVacancy && p.trackId) {
                    // Vacancy - use track ID
                    participantTrackIDs.push(p.trackId);
                } else if (p.id && !p.isVacancy) {
                    // Regular participant with user ID
                    participantUserIDs.push(p.id);
                } else if (p.email && !p.isVacancy) {
                    // New participant with email only
                    participantEmails.push(p.email);
                }
            });

            if (participantUserIDs.length === 0 && participantEmails.length === 0 && participantTrackIDs.length === 0) {
                setError('Please select at least one participant or enter an email');
                setSubmitting(false);
                return;
            }

            const trimmedLabel = (label || '').trim();
            const ruleData = {
                rule_type: ruleType,
                participant_user_ids: participantUserIDs,
            };
            if (trimmedLabel) {
                ruleData.label = trimmedLabel;
            } else {
                ruleData.label = null; // allow clearing label
            }

            // Include emails if any
            if (participantEmails.length > 0) {
                ruleData.participant_emails = participantEmails;
            }

            // Include track IDs for vacancies
            if (participantTrackIDs.length > 0) {
                ruleData.participant_track_ids = participantTrackIDs;
            }

            if (ruleType === 'minus') {
                ruleData.amount = parseFloat(amount);
            }

            if (ruleType === 'percent') {
                // Include rule_percentage in the update request
                if (rulePercentage && !isNaN(parseFloat(rulePercentage))) {
                    ruleData.rule_percentage = parseFloat(rulePercentage);
                }
                const percentages = {};
                selectedParticipants.forEach(p => {
                    const key = p.id || p.email;
                    if (key && participantPercentages[key] != null) {
                        // For vacancies, use track_id as the key; for users, use user_id or email
                        if (p.isVacancy && p.trackId) {
                            percentages[p.trackId] = participantPercentages[key];
                        } else {
                            const percentKey = p.id || p.email;
                            percentages[percentKey] = participantPercentages[key];
                        }
                    }
                });
                ruleData.participant_percentages = percentages;
            }

            await updateBillRule(String(templateId), rule.id, ruleData, csrfToken);

            // Invalidate and refetch rules and participants (in case new participants were added)
            await Promise.all([
                queryClient.invalidateQueries(['billRules', String(templateId)]),
                queryClient.invalidateQueries(['participants', String(templateId)]),
                queryClient.invalidateQueries(['estimatedSplits', String(templateId)]),
                queryClient.invalidateQueries(['billingTracks', String(templateId)])
            ]);

            if (onSuccess) {
                onSuccess();
            }

            handleClose();
        } catch (err) {
            console.error('Error updating rule:', err);
            setError(err.response?.data?.error || err.message || 'Failed to update rule');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!rule?.id) return;

        if (!window.confirm('Are you sure you want to delete this rule?')) {
            return;
        }

        setDeleting(true);
        try {
            await deleteBillRule(String(templateId), rule.id, csrfToken);
            queryClient.invalidateQueries(['billRules', String(templateId)]);
            queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);
            if (onDelete) {
                onDelete();
            }
            handleClose();
        } catch (err) {
            console.error('Error deleting rule:', err);
            setError(err.response?.data?.error || err.message || 'Failed to delete rule');
        } finally {
            setDeleting(false);
        }
    };

    if (!rule) {
        return null;
    }

    // Render as inline table rows when in inline mode
    if (inlineMode) {
        const isValid = () => {
            if (ruleType === 'minus') {
                return amount && parseFloat(amount) > 0 && selectedParticipants.length > 0;
            } else if (ruleType === 'percent') {
                if (!rulePercentage || parseFloat(rulePercentage) <= 0 || selectedParticipants.length === 0) {
                    return false;
                }
                // Check if participant percentages add up to rule percentage
                const totalPct = calculateTotalPercentage();
                const rulePct = parseFloat(rulePercentage);
                return Math.abs(totalPct - rulePct) < 0.01; // Allow small floating point differences
            }
            return false;
        };

        const hasChanged = () => {
            if (!rule) return false;

            // Check if label changed
            if ((label || '').trim() !== (rule.label || '').trim()) return true;

            // Check if rule type changed
            if (ruleType !== rule.rule_type) return true;

            // Check if amount changed (for minus rules)
            if (ruleType === 'minus') {
                const currentAmount = amount ? parseFloat(amount) : null;
                const originalAmount = rule.amount ? parseFloat(rule.amount) : null;
                if (currentAmount !== originalAmount) return true;
            }

            // Check if rule percentage changed (for percent rules)
            if (ruleType === 'percent') {
                const currentPct = rulePercentage ? parseFloat(rulePercentage) : null;
                const originalPct = rule.rule_percentage ? parseFloat(rule.rule_percentage) : null;
                if (currentPct !== originalPct) return true;
            }

            // Check if participants changed (including vacancies)
            // Build keys for original participants (users and vacancies)
            const originalParticipantKeys = (rule.participants || [])
                .map(p => {
                    if (p.user?.id) {
                        return `user-${p.user.id}`;
                    } else if (p.track && (!p.track.user_id || p.track.user_id === '')) {
                        return `vacancy-${p.track.id || p.track_id}`;
                    }
                    return '';
                })
                .filter(key => key)
                .sort();
            
            // Build keys for current participants (users and vacancies)
            const currentParticipantKeys = selectedParticipants
                .map(p => {
                    if (p.isVacancy && p.trackId) {
                        return `vacancy-${p.trackId}`;
                    } else if (p.id) {
                        return `user-${p.id}`;
                    } else if (p.email) {
                        return `email-${p.email.toLowerCase()}`;
                    }
                    return '';
                })
                .filter(key => key)
                .sort();

            if (originalParticipantKeys.length !== currentParticipantKeys.length) return true;
            if (originalParticipantKeys.some((key, idx) => key !== currentParticipantKeys[idx])) return true;

            // Check if participant percentages changed (for percent rules)
            if (ruleType === 'percent') {
                // Check all original participants (users and vacancies) to see if their percentages changed
                for (const originalParticipant of (rule.participants || [])) {
                    let originalKey = null;
                    let originalPct = originalParticipant.percentage;
                    
                    if (originalParticipant.user?.id) {
                        originalKey = originalParticipant.user.id;
                    } else if (originalParticipant.track && (!originalParticipant.track.user_id || originalParticipant.track.user_id === '')) {
                        originalKey = `vacancy-${originalParticipant.track.id || originalParticipant.track_id}`;
                    }
                    
                    if (!originalKey) continue;
                    
                    const currentParticipant = selectedParticipants.find(p => {
                        if (originalKey.startsWith('vacancy-')) {
                            return p.isVacancy && p.trackId && `vacancy-${p.trackId}` === originalKey;
                        } else {
                            return !p.isVacancy && p.id === originalKey;
                        }
                    });
                    
                    const currentPct = currentParticipant ? participantPercentages[currentParticipant.id || currentParticipant.email] : null;
                    
                    // If participant was removed or percentage changed
                    if (!currentParticipant || currentPct !== originalPct) return true;
                }
                
                // Check if any new participants were added (not in original)
                for (const currentParticipant of selectedParticipants) {
                    const currentKey = currentParticipant.isVacancy && currentParticipant.trackId
                        ? `vacancy-${currentParticipant.trackId}`
                        : currentParticipant.id
                            ? `user-${currentParticipant.id}`
                            : null;
                    
                    if (!currentKey) continue;
                    
                    const originalParticipant = rule.participants?.find(p => {
                        if (currentKey.startsWith('vacancy-')) {
                            const trackId = currentKey.replace('vacancy-', '');
                            return p.track && (p.track.id === trackId || p.track_id === trackId) && (!p.track.user_id || p.track.user_id === '');
                        } else {
                            return p.user?.id === currentKey.replace('user-', '');
                        }
                    });
                    
                    if (!originalParticipant) return true; // New participant added
                }
            }

            return false;
        };

        return (
            <>
                {/* Header Row */}
                <TableRow>
                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0, borderBottom: 'none' }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 1, borderBottom: 'none' }}>
                        <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                            Edit Rule
                        </Typography>
                    </TableCell>
                    <TableCell sx={{
                        width: '56px !important',
                        minWidth: '56px !important',
                        maxWidth: '56px !important',
                        p: 0,
                        flexShrink: 0,
                        flexGrow: 0,
                        borderBottom: 'none',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%'
                        }}>
                            <IconButton
                                onClick={handleClose}
                                size="small"
                                sx={{
                                    color: 'inherit',
                                    padding: '4px',
                                    '&:hover': {
                                        backgroundColor: theme.colors.overlayHover,
                                    },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </TableCell>
                </TableRow>

                {/* Form Row */}
                <TableRow>
                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 2 }}>
                        <Box
                            component="form"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                        >
                            {error && (
                                <Alert severity="error" onClose={() => setError(null)}>
                                    {error}
                                </Alert>
                            )}

                            <TextField
                                label="Label (recommended)"
                                placeholder="e.g. pet fee"
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                fullWidth
                                helperText="Label rules so they're easier to recognize."
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme.colors.text,
                                        fontSize: '16px',
                                        '& fieldset': {
                                            borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                        },
                                        '&:hover fieldset': { borderColor: theme.colors.text },
                                        '&.Mui-focused fieldset': { borderColor: theme.colors.text },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme.colors.secondary || theme.colors.brandSecondary,
                                        fontSize: '16px',
                                        '&.Mui-focused': { color: theme.colors.text },
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: theme.colors.text,
                                        opacity: 0.8,
                                    },
                                }}
                            />

                            <FormControl fullWidth>
                                <InputLabel sx={{ color: theme.colors.secondary || theme.colors.brandSecondary, fontSize: '16px', '&.Mui-focused': { color: theme.colors.text } }}>Rule Type</InputLabel>
                                <Select
                                    value={ruleType}
                                    onChange={(e) => {
                                        const newRuleType = e.target.value;
                                        const oldRuleType = ruleType;
                                        setRuleType(newRuleType);

                                        // When switching from minus to percent, clear the previous rule percentage ref
                                        // so that the useEffect will initialize percentages evenly
                                        if (oldRuleType !== 'percent' && newRuleType === 'percent') {
                                            previousRulePercentageRef.current = null;

                                            // If we already have a rule percentage set, initialize participant percentages evenly
                                            if (rulePercentage && selectedParticipants.length > 0) {
                                                const rulePct = parseFloat(rulePercentage);
                                                if (!isNaN(rulePct) && rulePct > 0) {
                                                    const equalPercent = rulePct / selectedParticipants.length;
                                                    const newPercentages = {};
                                                    selectedParticipants.forEach(p => {
                                                        const key = p.id || p.email;
                                                        if (key) {
                                                            newPercentages[key] = equalPercent;
                                                        }
                                                    });
                                                    setParticipantPercentages(newPercentages);
                                                }
                                            }
                                        }
                                    }}
                                    label="Rule Type"
                                    sx={{
                                        color: theme.colors.text,
                                        fontSize: '16px',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.colors.text,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.colors.text,
                                        },
                                    }}
                                >
                                    <MenuItem value="minus">Minus (Fixed Amount)</MenuItem>
                                    <MenuItem value="percent">Percent Split</MenuItem>
                                </Select>
                            </FormControl>

                            {ruleType === 'minus' && (
                                <TextField
                                    label="Amount"
                                    type="number"
                                    value={amount}
                                    onChange={handleAmountChange}
                                    onKeyDown={handleAmountKeyDown}
                                    fullWidth
                                    inputProps={{ min: 0, step: 'any' }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.colors.text,
                                            fontSize: '16px',
                                            '& fieldset': {
                                                borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.colors.text,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.colors.text,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.colors.secondary || theme.colors.brandSecondary,
                                            fontSize: '16px',
                                            '&.Mui-focused': {
                                                color: theme.colors.text,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '16px',
                                        },
                                    }}
                                />
                            )}

                            {ruleType === 'percent' && (
                                <TextField
                                    label="Rule Percentage (of bill total)"
                                    type="number"
                                    value={rulePercentage}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === '' || !isNaN(parseFloat(value))) {
                                            setRulePercentage(value);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            const currentValue = rulePercentage === '' ? 0 : parseFloat(rulePercentage);
                                            const newValue = e.key === 'ArrowUp' ? currentValue + 1 : currentValue - 1;
                                            if (newValue >= 0 && newValue <= 100) {
                                                setRulePercentage(newValue.toString());
                                            }
                                        }
                                    }}
                                    fullWidth
                                    inputProps={{ min: 0, max: 100, step: 'any' }}
                                    required
                                    error={selectedParticipants.length > 0 && Math.abs(calculateTotalPercentage() - parseFloat(rulePercentage || 0)) >= 0.01}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.colors.text,
                                            fontSize: '16px',
                                            '& fieldset': {
                                                borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.colors.text,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.colors.text,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.colors.secondary || theme.colors.brandSecondary,
                                            fontSize: '16px',
                                            '&.Mui-focused': {
                                                color: theme.colors.text,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '16px',
                                        },
                                    }}
                                />
                            )}

                            {ruleType === 'percent' && selectedParticipants.length > 0 && (
                                <Box>
                                    <Button
                                        onClick={() => setShowAdvanced(!showAdvanced)}
                                        variant="text"
                                        size="small"
                                        sx={{
                                            color: theme.colors.secondary || theme.colors.brandSecondary,
                                            textTransform: 'none',
                                            mb: 1,
                                            '&:hover': {
                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
                                    </Button>
                                    {showAdvanced && (
                                        <>
                                            <ParticipantSlider
                                                participants={selectedParticipants}
                                                participantPercentages={participantPercentages}
                                                onPercentageChange={handlePercentageChange}
                                                theme={theme}
                                                maxPercentage={rulePercentage ? parseFloat(rulePercentage) : 100}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: (() => {
                                                        const total = calculateTotalPercentage();
                                                        const target = parseFloat(rulePercentage || 0);
                                                        return Math.abs(total - target) < 0.01 ? theme.colors.text : (theme.colors.error || '#f44336');
                                                    })(),
                                                    mt: 1,
                                                    fontWeight: (() => {
                                                        const total = calculateTotalPercentage();
                                                        const target = parseFloat(rulePercentage || 0);
                                                        return Math.abs(total - target) < 0.01 ? 400 : 600;
                                                    })()
                                                }}
                                            >
                                                {(() => {
                                                    const total = calculateTotalPercentage();
                                                    const target = parseFloat(rulePercentage || 0);
                                                    const diff = Math.abs(total - target);
                                                    return `Total: ${FormatNumber(total)}%${diff >= 0.01 ? ` (must equal ${rulePercentage}%)` : ''}`;
                                                })()}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            )}

                            {ruleType === 'percent' && selectedParticipants.length > 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {selectedParticipants.map((participant) => {
                                        const key = participant?.id || participant?.email;
                                        if (!key) return null;
                                        const percentage = participantPercentages[key] || 0;

                                        return (
                                            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                {showAdvanced ? (
                                                    <TextField
                                                        type="number"
                                                        value={percentage || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            const numValue = value === '' ? 0 : parseFloat(value);
                                                            if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                                                                handlePercentageChange(key, numValue);
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                                e.preventDefault();
                                                                const currentValue = percentage || 0;
                                                                const newValue = e.key === 'ArrowUp' ? currentValue + 1 : currentValue - 1;
                                                                if (newValue >= 0 && newValue <= 100) {
                                                                    handlePercentageChange(key, newValue);
                                                                }
                                                            }
                                                        }}
                                                        inputProps={{ min: 0, max: 100, step: 'any' }}
                                                        error={Math.abs(calculateTotalPercentage() - parseFloat(rulePercentage || 0)) >= 0.01}
                                                        sx={{
                                                            width: '80px',
                                                            '& .MuiOutlinedInput-root': {
                                                                color: theme.colors.text,
                                                                fontSize: '16px',
                                                                '& fieldset': {
                                                                    borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: theme.colors.text,
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: theme.colors.text,
                                                                },
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '16px',
                                                                textAlign: 'right',
                                                                paddingRight: '8px',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            width: '80px',
                                                            color: theme.colors.text,
                                                            fontSize: '16px',
                                                            textAlign: 'right',
                                                            paddingRight: '8px',
                                                        }}
                                                    >
                                                        {FormatNumber(percentage)}%
                                                    </Typography>
                                                )}
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        color: theme.colors.text, 
                                                        flex: 1, 
                                                        fontSize: '16px',
                                                        fontStyle: participant?.isVacancy ? 'italic' : 'normal',
                                                    }}
                                                >
                                                    {participant?.email || 'Unknown'}
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        const newParticipants = selectedParticipants.filter(p => {
                                                            const pKey = p?.id || p?.email;
                                                            return pKey !== key;
                                                        });
                                                        setSelectedParticipants(newParticipants);

                                                        // Remove from percentages and redistribute remaining
                                                        const newPercentages = { ...participantPercentages };
                                                        delete newPercentages[key];

                                                        if (newParticipants.length > 0) {
                                                            // Redistribute evenly among remaining participants
                                                            const rulePct = rulePercentage ? parseFloat(rulePercentage) : 100;
                                                            const equalPercent = rulePct / newParticipants.length;
                                                            newParticipants.forEach(p => {
                                                                const pKey = p?.id || p?.email;
                                                                if (pKey) {
                                                                    newPercentages[pKey] = equalPercent;
                                                                }
                                                            });
                                                        }

                                                        setParticipantPercentages(newPercentages);
                                                    }}
                                                    sx={{
                                                        minWidth: 'auto',
                                                        color: theme.colors.error || '#f44336',
                                                        '&:hover': {
                                                            backgroundColor: `${theme.colors.error || '#f44336'}10`,
                                                        },
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}

                            <Autocomplete
                                key={ruleType === 'percent' ? `percent-${selectedParticipants.length}` : 'minus'}
                                multiple={ruleType !== 'percent'}
                                freeSolo
                                options={(participants || []).filter(p => {
                                    if (!p || !p.id) return false;
                                    return !selectedParticipants.find(sp => sp && (sp.id === p.id || sp.email?.toLowerCase() === p.email?.toLowerCase()));
                                })}
                                getOptionLabel={(option) => {
                                    if (!option) return '';
                                    return typeof option === 'string' ? option : (option.email || '');
                                }}
                                value={ruleType === 'percent' ? null : (selectedParticipants || [])}
                                inputValue={ruleType === 'percent' ? participantInputValue : undefined}
                                onInputChange={ruleType === 'percent' ? (event, newInputValue) => {
                                    if (event) {
                                        setParticipantInputValue(newInputValue);
                                    }
                                } : undefined}
                                loading={isLoadingParticipants}
                                onChange={(event, newValue) => {
                                    if (ruleType === 'percent') {
                                        // For percent rules, add one participant at a time
                                        if (newValue) {
                                            const item = newValue;
                                            let newParticipant = null;

                                            if (typeof item === 'string') {
                                                const email = item.trim();
                                                if (email) {
                                                    newParticipant = { email };
                                                }
                                            } else if (item && item.id && item.email) {
                                                newParticipant = item;
                                            }

                                            if (newParticipant) {
                                                const email = newParticipant.email?.toLowerCase();
                                                const alreadyExists = selectedParticipants.find(sp => {
                                                    const spEmail = sp.email?.toLowerCase();
                                                    return spEmail === email || sp.id === newParticipant.id;
                                                });

                                                if (!alreadyExists) {
                                                    const updatedParticipants = [...selectedParticipants, newParticipant];
                                                    setSelectedParticipants(updatedParticipants);

                                                    // Redistribute percentages evenly
                                                    const rulePct = rulePercentage ? parseFloat(rulePercentage) : 100;
                                                    const equalPercent = rulePct / updatedParticipants.length;
                                                    const newPercentages = {};
                                                    updatedParticipants.forEach(p => {
                                                        const key = p.id || p.email;
                                                        if (key) {
                                                            newPercentages[key] = equalPercent;
                                                        }
                                                    });
                                                    setParticipantPercentages(newPercentages);
                                                    
                                                    // Clear the input field
                                                    setParticipantInputValue('');
                                                }
                                            }
                                        }
                                    } else {
                                        // For minus rules, handle multiple participants
                                        const processedParticipants = [];
                                        const seenEmails = new Set();

                                        newValue.forEach(item => {
                                            if (typeof item === 'string') {
                                                const email = item.trim().toLowerCase();
                                                if (email && !seenEmails.has(email)) {
                                                    processedParticipants.push({ email: item.trim() });
                                                    seenEmails.add(email);
                                                }
                                            } else if (item && item.id && item.email) {
                                                const email = item.email.toLowerCase();
                                                if (!seenEmails.has(email)) {
                                                    processedParticipants.push(item);
                                                    seenEmails.add(email);
                                                }
                                            }
                                        });

                                        setSelectedParticipants(processedParticipants);
                                    }
                                }}
                                renderTags={(value, getTagProps) =>
                                    ruleType !== 'percent' ? value.map((option, index) => {
                                        const email = option.email || option;
                                        const { key, ...tagProps } = getTagProps({ index });
                                        return (
                                            <Chip
                                                key={key}
                                                label={email}
                                                {...tagProps}
                                                sx={{ color: theme.colors.text }}
                                            />
                                        );
                                    }) : []
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={ruleType === 'percent' ? 'Add Participant' : 'Participants'}
                                        placeholder="Select or type email"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.colors.text,
                                                fontSize: '16px',
                                                '& fieldset': {
                                                    borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.colors.text,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.colors.text,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.colors.secondary || theme.colors.brandSecondary,
                                                fontSize: '16px',
                                                '&.Mui-focused': {
                                                    color: theme.colors.text,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />

                            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                <Button
                                    type={hasChanged() && isValid() ? "submit" : "button"}
                                    onClick={hasChanged() && isValid() ? undefined : handleClose}
                                    variant="outlined"
                                    fullWidth
                                    disabled={submitting}
                                    sx={{
                                        border: `1px solid ${hasChanged() && isValid() ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary) : (theme.colors.border || '#ccc')}`,
                                        backgroundColor: 'transparent',
                                        color: hasChanged() && isValid() ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary) : theme.colors.text,
                                        '&:hover': {
                                            borderColor: hasChanged() && isValid() ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary) : (theme.colors.border || '#ccc'),
                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                        },
                                    }}
                                >
                                    {hasChanged() && isValid() ? (submitting ? 'Saving...' : 'Done') : 'Cancel'}
                                </Button>
                            </Box>
                        </Box>
                    </TableCell>
                    <TableCell sx={{
                        width: '56px !important',
                        minWidth: '56px !important',
                        maxWidth: '56px !important',
                        p: 0,
                        flexShrink: 0,
                        flexGrow: 0,
                    }}></TableCell>
                </TableRow>
            </>
        );
    }

    // Dialog mode is no longer used - only inline mode is supported
    return null;
};

export default EditRuleForm;
